import {
  type AirExchangeForType,
  type AirExchangeShopRequest,
  AirExchangeShopResponseEnum,
} from "@b2bportal/air-exchange-api";
import { Platform, ShopFilter } from "@b2bportal/air-shopping-api";
import type { ShopSummaryResponseV4 } from "@b2bportal/air-shopping-api/lib/api";
import { TripCategory } from "@b2bportal/core-types";
import { flightsExchangeApi, getDeviceType } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { FlightState } from "../../../../store/slice";

interface FetchExchangeFlightsParams {
  originalReservationId: string;
  exchangeType: AirExchangeForType;
}

export const fetchExchangeFlights = createAsyncThunk<
  ShopSummaryResponseV4,
  FetchExchangeFlightsParams,
  { state: FlightState; rejectValue: { error: string } }
>(
  "flightsShop/fetchExchangeFlights",
  async ({ originalReservationId, exchangeType }, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      passengerCounts: {
        adultsCount,
        infantsInSeatCount,
        infantsOnLapCount,
        childrenCount,
      },
      origin,
      destination,
      departureDate,
      returnDate,
      tripCategory,
    } = state.flights.flightSearch;
    const { isDesktopAndUp } = getDeviceType();
    try {
      if (origin == null || destination == null || departureDate == null) {
        return thunkAPI.rejectWithValue({ error: "Missing required fields" });
      }
      const requestBody: AirExchangeShopRequest = {
        passengers: {
          ADT: Number(adultsCount),
          ...(infantsInSeatCount ? { INS: Number(infantsInSeatCount) } : {}),
          ...(infantsOnLapCount ? { INF: Number(infantsOnLapCount) } : {}),
          ...(childrenCount ? { CNN: Number(childrenCount) } : {}),
        },
        originalReservationId,
        exchangeType,
        route: {
          origin: origin.id.code,
          destination: destination.id.code,
        },
        tripFilter: ShopFilter.NoFilter,
        platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
        departureDate,
        ...(tripCategory === TripCategory.ROUND_TRIP
          ? { returnDate: returnDate }
          : {}),
      };

      const res =
        await flightsExchangeApi.apiV0SelfserveFlightExchangeShopPost(
          requestBody
        );
      const data = res.data;

      switch (data.AirExchangeShopResponse) {
        case AirExchangeShopResponseEnum.AirExchangeShopSuccess:
          return {
            flights: data.flights,
            trackingProperties: {},
          };
        case AirExchangeShopResponseEnum.AirExchangeShopFailure:
          return thunkAPI.rejectWithValue({
            error: "Error fetching exchange flights",
          });
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({ error: String(error) });
    }
  }
);
